import React, { useCallback, useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { errorText, formatPHP, formatWhen, METHOD_LABEL, saleWords } from "@/lib/gems";

// A plain merchant may change these four. The rest widen or narrow what is
// paid with nobody watching, so the server wants a super admin for them and
// the page does not offer a plain merchant a request it knows will be refused.
const MERCHANT_FIELDS = [
  ["first_sale_fee_php", "Fee on a player's first sale (PHP)"],
  ["fee_php", "Fee on each sale after (PHP)"],
  ["min_gems", "Minimum gems per sale"],
  ["max_gems", "Maximum gems per sale"],
];
const SUPER_FIELDS = [
  ["auto_max_per_sale_php", "Automatic limit per sale (PHP)"],
  ["auto_max_per_day_php", "Automatic limit per day (PHP)"],
];
const FIELD = "mt-1 w-full min-w-0 border border-border rounded-sm px-2 py-1.5 bg-background";
const SMALL_BTN = "v-btn-outline !px-3 !py-2 disabled:opacity-50 disabled:pointer-events-none";
const NOT_AVAILABLE = "Not available";

// null means the system that owns the figure could not be read. It is never
// zero: a headroom of nothing and a headroom nobody could measure are
// different facts to the person deciding whether to approve.
function peso(value) {
  return value === null || value === undefined ? NOT_AVAILABLE : formatPHP(value);
}

function whole(value) {
  return Number(value || 0).toLocaleString("en-PH");
}

function Tiles({ summary }) {
  if (!summary) return null;
  const tiles = [
    ["kmax_cashin_php", "Cash-in received by ePlayQuest", summary.kmax_cashin_php],
    ["committed_php", "Committed (paid or on its way)", summary.committed_php],
    ["in_flight_php", "In flight", summary.in_flight_php],
    ["headroom_php", "Headroom", summary.headroom_php],
    ["merchant_balance_php", "KMAX merchant balance", summary.merchant_balance_php],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="exchange-tiles">
        {tiles.map(([key, label, value]) => (
          <div key={key} className="v-card !p-4 min-w-0" data-testid={`tile-${key}`}>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="mt-1 font-display text-lg sm:text-xl font-bold break-words">{peso(value)}</div>
          </div>
        ))}
      </div>
      {summary.eplq_reachable === false && (
        <p className="mt-2 text-sm text-muted-foreground">
          ePlayQuest could not be reached, so cash-in and headroom are not available.
        </p>
      )}
      {summary.kmax_reachable === false && (
        <p className="mt-1 text-sm text-muted-foreground">KMAX could not be reached, so its balance is not available.</p>
      )}
    </div>
  );
}

function canDecide(row) {
  return row.state === "review" || (row.state === "requested" && row.eplq_sale_state === "settled");
}

function ReviewRow({ row, locked, onAction }) {
  const [reason, setReason] = useState("");
  return (
    <li className="v-card !p-4" data-testid={`review-${row.id}`}>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="font-semibold break-words">{row.seller_email}</div>
          <div className="text-sm text-muted-foreground break-words">
            {whole(row.gems)} gems for {formatPHP(row.payout_php)} to {METHOD_LABEL[row.method] || row.method}{" "}
            {row.mobile_masked}
          </div>
        </div>
        <div className="text-sm">{formatWhen(row.created_at)}</div>
      </div>
      <p className="mt-2 text-sm break-words" data-testid={`attention-${row.id}`}>{row.attention}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canDecide(row) && (
          <>
            <button type="button" className="v-btn-primary !px-4 !py-2" disabled={locked}
              onClick={() => onAction(row, "approve", {})}>Approve</button>
            <input name={`reason-${row.id}`} value={reason} placeholder="Why decline" maxLength={500}
              aria-label={`Reason for declining the sale by ${row.seller_email}`}
              className="min-w-0 flex-1 basis-40 border border-border rounded-sm px-2 py-1.5 text-sm bg-background"
              onChange={(e) => setReason(e.target.value)} />
            <button type="button" className={SMALL_BTN} disabled={locked || reason.trim().length < 3}
              onClick={() => onAction(row, "reject", { reason: reason.trim() })}>Decline</button>
          </>
        )}
        {row.state === "unknown" && (
          <button type="button" className={SMALL_BTN} disabled={locked}
            onClick={() => onAction(row, "resolve", {})}>Resolve with KMAX</button>
        )}
        <button type="button" className={SMALL_BTN} disabled={locked}
          onClick={() => onAction(row, "recheck", {})}>Recheck</button>
      </div>
      {row.state === "unknown" && (
        <p className="mt-2 text-xs text-muted-foreground">
          This asks KMAX what became of the payout. If KMAX says it was paid or failed, the sale settles. If KMAX has
          no record of it, only a super admin can release it, and only after 30 minutes.
        </p>
      )}
    </li>
  );
}

function wholeNumber(text) {
  const s = String(text ?? "").trim();
  return /^\d+$/.test(s) ? Number(s) : null;
}

function SettingsCard({ settings, isSuper, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saving = useRef(false);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings]);

  if (!form) return null;

  const fields = isSuper ? [...MERCHANT_FIELDS, ...SUPER_FIELDS] : MERCHANT_FIELDS;

  const save = async () => {
    if (saving.current) return;
    const body = {};
    if (isSuper) {
      body.enabled = !!form.enabled;
      body.auto_buy = !!form.auto_buy;
    }
    for (const [key, label] of fields) {
      const n = wholeNumber(form[key]);
      if (n === null) {
        setError(`${label} must be a whole number`);
        return;
      }
      body[key] = n;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch("/exchange/merchant/settings", body);
      toast.success("Settings saved");
      onSaved(data);
    } catch (err) {
      const message = errorText(err, "Settings were not saved");
      setError(message);
      toast.error(message);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="v-card" data-testid="exchange-settings">
      <h2 className="font-display text-lg font-bold">Settings</h2>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input type="checkbox" name="enabled" className="mt-1" checked={!!form.enabled} disabled={!isSuper}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        <span>Gem Exchange open</span>
      </label>
      <label className="mt-2 flex items-start gap-2 text-sm">
        <input type="checkbox" name="auto_buy" className="mt-1" checked={!!form.auto_buy} disabled={!isSuper}
          onChange={(e) => setForm({ ...form, auto_buy: e.target.checked })} />
        <span>Buy automatically inside the limits</span>
      </label>
      {!isSuper && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="settings-super-note">
          Only a super admin can open or close the exchange, switch automatic buying on or off, or change its limits.
          You can change the two fees and the minimum and maximum gems per sale.
        </p>
      )}
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        {MERCHANT_FIELDS.map(([key, label]) => (
          <label key={key} className="text-sm min-w-0">
            {label}
            <input name={key} value={form[key] ?? ""} inputMode="numeric" className={FIELD}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          </label>
        ))}
        {SUPER_FIELDS.map(([key, label]) => (
          <label key={key} className="text-sm min-w-0">
            {label}
            <input name={key} value={form[key] ?? ""} inputMode="numeric" className={FIELD} disabled={!isSuper}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          </label>
        ))}
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      <button type="button" className="v-btn-primary mt-4" disabled={busy} onClick={save}>Save settings</button>
    </section>
  );
}

function Reconciliation({ summary }) {
  if (!summary) return null;
  const sold = summary.gems_sold_total;
  return (
    <section className="v-card" data-testid="exchange-reconciliation">
      <h2 className="font-display text-lg font-bold">Reconciliation</h2>
      <p className="mt-2 text-sm">
        Vendora has paid {peso(summary.paid_php)} for {whole(summary.paid_gems)} gems.
      </p>
      <p className="mt-1 text-sm">
        {sold === null || sold === undefined
          ? "ePlayQuest's totals cannot be read right now."
          : `ePlayQuest records ${whole(sold)} gems sold and ${whole(summary.gems_refunded_total)} returned.`}
      </p>
    </section>
  );
}

export default function ExchangeQueue() {
  const { user } = useAuth();
  const isSuper = !!user?.roles?.includes("super_admin");
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  // The ref is the lock; the state only redraws the buttons. A second click in
  // the same tick would otherwise see the old state and send a second request.
  const working = useRef(false);

  // The float and the queue move under the page; the settings form is only
  // re-read at the start and after a save, so a refresh never wipes a number
  // the person is half way through typing.
  const refresh = useCallback(async () => {
    const [s, q] = await Promise.allSettled([api.get("/exchange/merchant/summary"), api.get("/exchange/merchant/queue")]);
    if (s.status === "fulfilled") setSummary(s.value.data);
    if (q.status === "fulfilled") setQueue(q.value.data);
    const failed = [s, q].find((r) => r.status === "rejected");
    setError(failed ? errorText(failed.reason, "The queue could not be loaded") : "");
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/exchange/merchant/settings");
      setSettings(data);
    } catch (err) {
      setError(errorText(err, "The settings could not be loaded"));
    }
  }, []);

  useEffect(() => {
    refresh();
    loadSettings();
  }, [refresh, loadSettings]);

  const run = async (call) => {
    if (working.current) return;
    working.current = true;
    setLocked(true);
    try {
      await call();
    } catch (err) {
      toast.error(errorText(err, "That did not work"));
    }
    try {
      await refresh();
    } finally {
      working.current = false;
      setLocked(false);
    }
  };

  const onAction = (row, action, body) => run(async () => {
    const { data } = await api.post(`/exchange/merchant/sales/${row.id}/${action}`, body);
    toast.success(`${row.seller_email}: ${saleWords(data) || "Done"}`);
  });

  const sweep = () => run(async () => {
    await api.post("/exchange/merchant/sweep", {});
    toast.success("Checked every open sale");
  });

  const review = queue?.needs_review || [];
  const sales = queue?.sales || [];
  const reviewTotal = queue?.needs_review_total ?? review.length;
  return (
    <DashboardLayout title="Gem Exchange queue" subtitle="The Gem Collector: sales that need a person first, then every sale">
      <div className="w-full max-w-5xl space-y-8">
        {error && <p role="alert" className="text-destructive">{error}</p>}
        <Tiles summary={summary} />
        <section data-testid="needs-review">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl font-bold">Needs review</h2>
            <button type="button" className={SMALL_BTN} disabled={locked} onClick={sweep}>Check all open sales now</button>
          </div>
          {queue && reviewTotal > review.length && (
            <p className="mt-1 text-sm text-muted-foreground">Showing {review.length} of {reviewTotal} that need a person, oldest first</p>
          )}
          {review.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing needs a person right now.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {review.map((row) => <ReviewRow key={row.id} row={row} locked={locked} onAction={onAction} />)}
            </ul>
          )}
        </section>
        <section data-testid="all-sales">
          <h2 className="font-display text-xl font-bold">All sales</h2>
          {queue && <p className="text-sm text-muted-foreground">Showing {queue.shown} of {queue.total}</p>}
          <ul className="mt-3 divide-y divide-border text-sm">
            {sales.map((row) => (
              <li key={row.id} className="py-3 space-y-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="font-semibold break-words">{row.seller_email}</span>
                  <span className="text-muted-foreground">{formatWhen(row.created_at)}</span>
                </div>
                <div>{whole(row.gems)} gems, {formatPHP(row.payout_php)}</div>
                <div className="text-muted-foreground break-words">
                  {saleWords(row)}{row.reject_reason ? `: ${row.reject_reason}` : ""}
                </div>
              </li>
            ))}
          </ul>
        </section>
        <div className="grid lg:grid-cols-2 gap-6">
          <SettingsCard settings={settings} isSuper={isSuper} onSaved={setSettings} />
          <Reconciliation summary={summary} />
        </div>
      </div>
    </DashboardLayout>
  );
}
